import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReusableTable from "../../common/table/CommonTable";
import { deleteStaff, fetchStaff } from "../../redux/staff/staffAction";

const ListStaff = () => {
  const [loading, setLoading] = useState(false);
  const [openDeleteModel, setOpenDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const searchTimeoutRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const staffState = useSelector(
    (state) => state?.staffs?.fetchListStaff || {}
  );
  const deleteStaffState = useSelector(
    (state) => state?.staffs?.deleteStaff || {}
  );

  const staffList = staffState?.data?.data || [];
  const totalPages = staffState?.data?.totalPages || 0;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const payload = {
          pageNo: currentPage,
          pagesize: 10,
          pagination_required: true,
          search: searchQuery,
        };
        await dispatch(fetchStaff(payload));
      } catch (error) {
        console.error("Error fetching staff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, searchQuery, dispatch]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Enhanced search with debouncing
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
      setCurrentPage(1); // Reset to first page when searching
    }, 1000);
  };

  // Transform data and filter based on search query
  const filteredData = Array.isArray(staffList)
    ? staffList
        .map((staff) => ({
          ids: staff.id || "N/A",
          id: staff.staff_id || "N/A",
          name: staff.staff_name || "N/A",
          email: staff.email || "N/A",
          phone: staff.phone || "N/A",
          status: staff?.User?.status === 1 ? "Active" : "Inactive",
        }))
        .filter((staff) => {
          if (!searchQuery) return true;
          const query = searchQuery.toLowerCase();
          return (
            staff.id.toLowerCase().includes(query) ||
            staff.name.toLowerCase().includes(query) ||
            staff.email.toLowerCase().includes(query) ||
            staff.phone.toLowerCase().includes(query) ||
            staff.status.toLowerCase().includes(query)
          );
        })
    : [];

  const columns = [
    {
      id: "id",
      label: "Staff ID",
      sortable: true,
      render: (value, row) => (
        <Typography
          component="span"
          sx={{
            cursor: "pointer",
            color: "dodgerblue",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
          onClick={() => handleViewClick(row)}
        >
          {value}
        </Typography>
      ),
    },
    { id: "name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "phone", label: "Phone Number", sortable: true },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          color={value === "Active" ? "success" : "warning"}
        />
      ),
    },
  ];

  const handleEditClick = (row) => {
    navigate(`/staff/edit/${row.ids}`);
  };

  const handleViewClick = (row) => {
    navigate(`/staff/view/${row.ids}`);
  };

  const handleDeleteClick = (row) => {
    console.log("Delete clicked for row:", row);
    console.log("Row IDs:", row.ids);
    setDeleteId(row.ids);
    setOpenDeleteModel(true);
  };

  const handleDeleteConfirm = async () => {
    console.log("Deleting ID:", deleteId);
    if (deleteId) {
      setLoading(true);
      try {
        await dispatch(deleteStaff(deleteId));
        setOpenDeleteModel(false);
        const payload = {
          pageNo: currentPage,
          pagesize: 10,
          pagination_required: true,
          search: searchQuery,
        };
        await dispatch(fetchStaff(payload));
        setDeleteId(null);
      } catch (error) {
        console.error("Error deleting staff:", error);
      } finally {
        setLoading(false);
      }
    } else {
      console.error("No delete ID available");
    }
  };

  const handleDeleteCancel = () => {
    setDeleteId(null);
    setOpenDeleteModel(false);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <TextField
          placeholder="Search staff by ID, name, email, phone, or status..."
          variant="outlined"
          size="small"
          value={searchInputValue}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: "400px" }}
        />
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : filteredData.length === 0 && searchQuery ? (
        <Typography>No records found</Typography>
      ) : (
        <ReusableTable
          title="Staffs"
          columns={columns}
          data={filteredData}
          isLoading={loading}
          headerColor="#000"
          rowColor="#ffffff"
          alternateRowColor="#f5f5f5"
          onViewClick={handleViewClick}
          onEditClick={handleEditClick}
          onDeleteClick={handleDeleteClick}
          withPagination={true}
          withActions={true}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          showDownloadOption={true}
        />
      )}

      <Modal
        open={openDeleteModel}
        style={{ left: "35%", top: "35%" }}
        onClose={handleDeleteCancel}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            width: "500px",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
            padding: "30px",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
            border: "1px solid #e0e0e0",
          }}
        >
          <Typography
            id="modal-modal-title"
            variant="h5"
            sx={{
              fontSize: "1.4rem",
              fontWeight: "600",
              color: "#d32f2f",
              marginBottom: "10px",
              textAlign: "center",
            }}
            component="h2"
          >
            ⚠️ Delete Staff Member
          </Typography>
          <Typography
            id="modal-modal-description"
            sx={{
              mt: 2,
              mb: 3,
              fontSize: "1rem",
              color: "#424242",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            Are you sure you want to delete this staff member?
            <br />
            <strong>This action cannot be undone.</strong>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mt: 4,
            }}
          >
            <Button
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={loading}
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#b71c1c",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(211,47,47,0.4)",
                },
                "&:disabled": {
                  backgroundColor: "#bdbdbd",
                  color: "#ffffff",
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading ? "Deleting..." : "Delete"}
            </Button>
            <Button
              variant="outlined"
              onClick={handleDeleteCancel}
              disabled={loading}
              sx={{
                borderColor: "#757575",
                color: "#757575",
                padding: "10px 25px",
                borderRadius: "8px",
                fontWeight: "600",
                textTransform: "none",
                "&:hover": {
                  borderColor: "#424242",
                  color: "#424242",
                  backgroundColor: "rgba(0,0,0,0.04)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  borderColor: "#bdbdbd",
                  color: "#bdbdbd",
                },
                transition: "all 0.3s ease",
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ListStaff;
